import { Icon } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  View as RNView,
  ScrollView,
  StyleProp,
  TextStyle,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { View } from '@shared/components/View';
import { MBBottomSheet, MBBottomSheetTitleBar } from '@shared/components/bottomSheet';
import { MBIconV2 } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextBody1Bold, MBTextOverlineBold } from '@shared/components/typography';
import { DateField } from '@shared/data/entity/fields/DateField';
import { ScheduledAutomationField } from '@shared/data/entity/fields/ScheduledAutomationField';
import { useGetWorkflowAutomation } from '@shared/scenes/automation/hooks/useGetWorkflowAutomation';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { EditFieldContainer } from '@shared/scenes/entity/components/fields/EditFieldContainer';
import { DateComponent, ScheduledAutomationComponent } from '@shared/scenes/workflows/config';
import { Divider } from '@shared/scenes/workflows/edit/components/optionsMenu/divider';
import { UnitMenu } from '@shared/scenes/workflows/edit/components/optionsMenu/scheduledAutomationOptions/UnitMenu';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isDateComponent, isInputComponent } from '@shared/scenes/workflows/utils';
import { Colors, cursorPointerStyle, grid } from '@shared/styles';
import {
  DEFAULT_COUNT_STR,
  TIME_OF_EVENT_DURATION,
  dedupeDurations,
  humanReadableDuration,
  unitListItems,
} from '@shared/util/durations';
import { useIsDesktop } from '@shared/util/hooks';
import { I18nKey, i18n } from '@shared/util/i18n';

interface Props {
  field: ScheduledAutomationField | null;
  component: MBAPI.WorkflowTemplate_components_ScheduledAutomationComponent;
  components?: MBAPI.WorkflowTemplate_components[];
  dateComponentField?: DateField;
  updateField: (componentId: string, field: ScheduledAutomationField | null) => void;
  isReadOnly?: boolean;
  showLabel?: boolean;
  overrides?: MBAPI.ViewTemplate_componentsViewOptions_overrides;
  error?: string;
  includeWrapperPadding?: boolean;
  allowClear?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function ScheduledAutomationEditFieldNoCodeWrapper(
  props: Props & {
    workflowTemplateId: string;
    style?: StyleProp<ViewStyle>;
  },
) {
  const { workflowTemplate } = useWorkflowTemplate(props.workflowTemplateId);
  return <ScheduledAutomationEditField {...props} components={workflowTemplate?.components} />;
}

export function ScheduledAutomationEditField({
  field,
  component,
  components,
  dateComponentField,
  updateField,
  isReadOnly,
  showLabel = false,
  error,
  includeWrapperPadding = true,
  allowClear = true,
  style,
}: Props) {
  const dateComponent = components
    ?.filter(isDateComponent)
    .find(c => c.id === component.dateComponentId);
  const dateFieldValue = dateComponentField?.date;
  const [showMenu, setShowMenu] = useState(false);
  const isMisconfigured = !component.automationId || !component.dateComponentId;

  const durations = useMemo(
    () => field?.durations.filter((v, i) => field?.durations.indexOf(v) === i) ?? [],
    [field],
  );

  const readOnly = isReadOnly || !!component.readonly || isMisconfigured;
  const anchorName = `--scheduled-automation-${component.id}`;
  const showClearButton = !isEmpty(durations) && !readOnly && allowClear;

  const handleChange = useCallback(
    (changedDurations: string[]) => {
      const deduped = dedupeDurations(changedDurations);
      updateField(component.id, !isEmpty(deduped) ? new ScheduledAutomationField(deduped) : null);
    },
    [component.id, updateField],
  );

  const onClear = useCallback(() => {
    updateField(component.id, null);
  }, [updateField, component.id]);

  const responsiveForms = useResponsiveFormContext();

  if (!isInputComponent(component)) {
    return null;
  }

  if (readOnly && isEmpty(durations)) {
    return null;
  }

  const hasContent = !isEmpty(durations);

  const handlePress = () => {
    setShowMenu(prev => !prev);
  };

  return (
    <>
      <EditFieldContainer
        component={component}
        anchorName={anchorName}
        showLabel={!isNil(showLabel) ? showLabel : responsiveForms || hasContent}
        error={error}
        includeWrapperPadding={includeWrapperPadding}
        onPress={isReadOnly ? undefined : handlePress}
        showClearButton={showClearButton}
        onClear={onClear}
        subfooter={
          <WarningSubfooter
            showWarning={isMisconfigured}
            message={i18n('workflowTemplate.component.error.misconfigured')}
          />
        }
        isGrayScale={isMisconfigured}
        style={style}
      >
        <View paddingVertical={1.25}>
          {isEmpty(durations) && (
            <MBTextBody1 style={{ color: Colors.v2.gray50 }}>{component?.placeholder}</MBTextBody1>
          )}
          {!isEmpty(durations) && <DurationsTextView durations={durations} />}
        </View>
      </EditFieldContainer>
      {showMenu && (
        <DurationsMenu
          component={component}
          dateComponent={dateComponent}
          dateFieldValue={dateFieldValue ?? ''}
          anchorName={anchorName}
          durations={durations}
          onChange={handleChange}
          onClose={() => setShowMenu(false)}
        />
      )}
    </>
  );
}

export function DurationsTextView({
  prefix,
  durations,
  textStyle,
}: {
  prefix?: string;
  durations: string[];
  textStyle?: StyleProp<TextStyle>;
}) {
  const humanReadableDurations = durations?.map(humanReadableDuration) ?? [];

  if (isEmpty(durations)) {
    return null;
  }

  return (
    <MBTextBody1 style={textStyle}>
      {!isEmpty(prefix) && `${prefix} `}
      {humanReadableDurations.join(', ')}
    </MBTextBody1>
  );
}

interface DurationsMenuProps {
  component: ScheduledAutomationComponent;
  dateComponent?: DateComponent;
  dateFieldValue: string;
  durations: string[];
  anchorName: string;
  onChange(durations: string[]): void;
  onClose(): void;
}

function DurationsMenu({
  component,
  dateComponent,
  dateFieldValue,
  durations,
  anchorName,
  onChange,
  onClose,
}: DurationsMenuProps) {
  const isDesktop = useIsDesktop();
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const { automation } = useGetWorkflowAutomation({
    automationId: component.automationId ?? undefined,
  });

  const handleClose = useCallback(() => {
    if (isAddingCustom) {
      setIsAddingCustom(false);
    } else {
      onClose();
    }
  }, [isAddingCustom, onClose]);

  const handleAddCustom = useCallback(
    (duration: string, index) => {
      const updatedDurations = [...(durations ?? [])];
      updatedDurations[index] = duration;
      onChange(updatedDurations);
    },
    [onChange, durations],
  );

  const renderTitleBar = useCallback(() => {
    if (isDesktop && !isAddingCustom) {
      return null;
    }
    if (isDesktop) {
      return (
        <MBBottomSheetTitleBar
          title={i18n('builder.scheduledAutomation.customOption')}
          titleSize={16}
          leftIcon="arrow-point-left"
          leftIconColor={Colors.v2.purple}
          leftIconSize={grid(3.5)}
          onLeftIconPress={handleClose}
          height={grid(6)}
        />
      );
    }
    const bottomSheetTitle = isAddingCustom
      ? i18n('builder.scheduledAutomation.customOption')
      : component.label ?? '';

    return (
      <MBBottomSheetTitleBar
        title={bottomSheetTitle}
        leftIcon="arrow-point-left"
        leftIconColor={Colors.v2.purple}
        leftIconSize={grid(4)}
        leftTitlePosition={grid(8)}
        rightTitlePosition={grid(8)}
        onLeftIconPress={handleClose}
        borderBottomWidth={1}
      />
    );
  }, [isAddingCustom, component.label, handleClose, isDesktop]);

  if (isNil(component) || isNil(dateComponent) || isNil(automation)) {
    return null;
  }

  return (
    <MBBottomSheet anchorName={anchorName} onClose={onClose} renderTitleBar={renderTitleBar}>
      <ScrollView>
        {!isAddingCustom && (
          <View
            flex
            padding={2}
            style={{
              width: isDesktop ? grid(44) : '100%',
              justifyContent: 'flex-start',
            }}
          >
            <View row centerVertically>
              <View marginRight={2}>
                <MBIconV2
                  name={dateComponent.icon as Icon}
                  color={Colors.v2.green}
                  size={grid(5)}
                />
              </View>
              <View>
                {!isEmpty(dateFieldValue) && (
                  <MBTextOverlineBold color={Colors.v2.gray30}>
                    {dateComponent.label}
                  </MBTextOverlineBold>
                )}
                <MBTextBody1>
                  {!isEmpty(dateFieldValue)
                    ? moment(dateFieldValue).format('MMM D, YYYY, h:mm a')
                    : dateComponent.placeholder}
                </MBTextBody1>
              </View>
            </View>
            {isEmpty(durations) && (
              <View paddingTop={2}>
                <Divider />
              </View>
            )}

            <SelectedOptions durations={durations} />
            <Presets durations={durations} onChange={onChange} />
            <View marginVertical={1}>
              <MBTextBody1Bold>{i18n('builder.scheduledAutomation.custom')}</MBTextBody1Bold>
            </View>
            <Customs durations={durations} onChange={onChange} />
            <AddCustom onPress={() => setIsAddingCustom(true)} />

            <View paddingTop={2} paddingBottom={1}>
              <Divider />
            </View>
            <View marginBottom={2}>
              <MBTextBody1Bold>{i18n('builder.scheduledAutomation.moreInfo')}</MBTextBody1Bold>
            </View>
            <View row paddingBottom={2}>
              <View marginRight={2}>
                <MBIconV2 name="lightning-bolt" color={Colors.v2.green} size={grid(5)} />
              </View>
              <View>
                <MBTextOverlineBold color={Colors.v2.gray30}>
                  {i18n('builder.scheduledAutomation.automationLabel')}
                </MBTextOverlineBold>
                <MBTextBody1>{automation.name}</MBTextBody1>
              </View>
            </View>
          </View>
        )}
        {isAddingCustom && (
          <CustomDuration onChange={handleAddCustom} index={durations?.length ?? 0} />
        )}
      </ScrollView>
    </MBBottomSheet>
  );
}

function SelectedOptions({ durations }: { durations: string[] }) {
  if (isEmpty(durations)) {
    return null;
  }
  return (
    <RNView
      style={{
        backgroundColor: Colors.v2.green08,
        marginHorizontal: -grid(2),
        paddingVertical: grid(1),
        paddingHorizontal: grid(2.5),
        marginTop: grid(1.5),
      }}
    >
      <DurationsTextView
        durations={durations}
        prefix={i18n('builder.scheduledAutomation.selectedOptions')}
      />
    </RNView>
  );
}

// https://app.shortcut.com/coast/story/41300/automations-card-view-support-for-new-scheduledautomation-component#activity-42123
const presetsList = [TIME_OF_EVENT_DURATION, '-PT10M', '-PT1H', '-P1D', 'PT1H', 'P1D', 'P7D'];

function Presets({
  durations,
  onChange,
}: {
  durations: string[];
  onChange(durations: string[]): void;
}) {
  const handleSelect = (preset: string) => {
    const alreadySelected = durations?.includes(preset);
    if (alreadySelected) {
      onChange(durations.filter(d => d !== preset));
    } else {
      onChange([...durations, preset]);
    }
  };

  return (
    <View>
      {presetsList.map(preset => (
        <TouchableOpacity
          key={preset}
          onPress={() => handleSelect(preset)}
          style={{ paddingVertical: grid(0.5), flexDirection: 'row', alignItems: 'center' }}
        >
          <View marginRight={2}>
            <MBIconV2
              name={!isEmpty(durations) && durations.includes(preset) ? 'check-filled' : 'circle'}
              color={Colors.v2.green}
              size={grid(5)}
            />
          </View>
          <MBTextBody1>{humanReadableDuration(preset)}</MBTextBody1>
        </TouchableOpacity>
      ))}
    </View>
  );
}

function Customs({
  durations,
  onChange,
}: {
  durations: string[];
  onChange(durations: string[]): void;
}) {
  const customDurations = durations.filter(d => !presetsList.includes(d));
  const handleSelect = (duration: string) => {
    onChange(durations.filter(d => d !== duration));
  };

  return (
    <View>
      {customDurations.map(duration => (
        <TouchableOpacity
          key={duration}
          onPress={() => handleSelect(duration)}
          style={{ paddingVertical: grid(0.5), flexDirection: 'row', alignItems: 'center' }}
        >
          <View marginRight={2}>
            <MBIconV2 name="check-filled" color={Colors.v2.green} size={grid(5)} />
          </View>
          <MBTextBody1>{humanReadableDuration(duration)}</MBTextBody1>
        </TouchableOpacity>
      ))}
    </View>
  );
}

function AddCustom({ onPress }: { onPress(): void }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={{ paddingVertical: grid(0.5), flexDirection: 'row', alignItems: 'center' }}
    >
      <View marginRight={2}>
        <MBIconV2 name="plus-circle" color={Colors.v2.green} size={grid(5)} />
      </View>
      <MBTextBody1>{i18n('builder.scheduledAutomation.addOption')}</MBTextBody1>
    </TouchableOpacity>
  );
}

function CustomDuration({
  index,
  onChange,
}: {
  onChange(duration: string, index: number): void;
  index: number;
}) {
  const cachedIndex = useRef(index);
  const [number, setNumber] = useState(DEFAULT_COUNT_STR);
  const [unit, setUnit] = useState('-PTnM');
  const [showingUnits, setShowingUnits] = useState(false);
  const isTimeOfEvent = unit === TIME_OF_EVENT_DURATION;

  const handleNumberChange = useCallback(
    (numberValue: string) => {
      if (isEmpty(numberValue)) {
        onChange(unit.replace('n', DEFAULT_COUNT_STR), cachedIndex.current);
        setNumber('');
        return;
      }
      setNumber(numberValue);
      onChange(unit.replace('n', numberValue), cachedIndex.current);
    },
    [unit, onChange],
  );

  const handleUnitChange = useCallback(
    (selectedUnit: string) => {
      setUnit(selectedUnit);
      setShowingUnits(false);
      onChange(selectedUnit.replace('n', number), cachedIndex.current);
    },
    [number, onChange],
  );

  return (
    <View style={{ minWidth: grid(48) }} padding={2}>
      {!showingUnits && (
        <View row centerVertically>
          <View marginRight={2}>
            <MBIconV2 name="time-clock" color={Colors.v2.green} size={grid(5)} />
          </View>
          {!isTimeOfEvent && (
            <MBTextInput
              value={number}
              onChangeText={handleNumberChange}
              style={{
                width: grid(6),
                height: grid(5),
                backgroundColor: Colors.v2.gray05,
                marginRight: grid(1),
                paddingVertical: grid(1),
                paddingHorizontal: grid(1),
              }}
            />
          )}
          <TouchableOpacity
            onPress={() => setShowingUnits(true)}
            style={[
              {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: Colors.v2.gray05,
                borderRadius: grid(0.5),
                height: grid(5),
                flexGrow: 1,
                paddingHorizontal: grid(2),
                paddingVertical: grid(1),
              },
              cursorPointerStyle,
            ]}
          >
            <View marginRight={2}>
              <MBTextBody1>
                {i18n((unitListItems.find(i => i.id === unit)?.label ?? '') as I18nKey, {
                  count: !isEmpty(number) ? Number(number) : 0,
                })}
              </MBTextBody1>
            </View>
            <MBIconV2 color={Colors.v2.purple} name="arrow-point-down-heavy" size={grid(1.25)} />
          </TouchableOpacity>
        </View>
      )}
      {showingUnits && (
        <UnitMenu
          count={!isEmpty(number) ? Number(number) : 0}
          onUnitSelect={handleUnitChange}
          showRadios
          selectedItem={unit}
        />
      )}
    </View>
  );
}
